// @ts-ignore
import mapValuesDeep from 'deepdash/mapValuesDeep'
import isPlainObject from 'lodash/isPlainObject'
import has from 'lodash/has'
import MyMenuAPIs from '@/API/my-menu/my-menu-api'
import LocalizationService from '@/services/localization-service'
import MyMenuService from '@/services/my-menu-service'
import MarketingService, { MARKETING_EVENTS } from '@/services/marketing-service'

export default {
  namespaced: true,
  state: {
    menu: [],
    editOrderMenu: [],
    filteredMenu: [],
    itemsCount: 0,
    menuFetched: false,
    provider: 'okMenu',
    menuBranchId: null,
    query: '',
    sort: 'original',
    filters: [],
    availableStorage: -1,
    totalStorage: -1,
    // drag-n-drop
    dragging: false,
    targetDraggingId: null,
    sourceDraggingId: null,
    addedDragEvent: null,
    removedDragEvent: null,
    isPOSMenu: false,
  },
  getters: {
    menu: (state: any) => state.menu,
    query: (state: any) => state.query,
    editOrderMenu: (state: any) => state.editOrderMenu,
    sort: (state: any) => state.sort,
    filters: (state: any) => state.filters,
    filteredMenu: (state: any) => state.filteredMenu ?? [],
    flatCategories: (state: any) => MyMenuService.flatCategories(state.menu) ?? [],
    flatItems: (state: any) => MyMenuService.flatItems(state.menu) ?? [],
    itemsCount: (state: any, getters: any) => getters.flatItems.length,
    hasFilters: (state: any) => !!state.query || state.filters?.length,
    availableStorage: (state: any) => (state.totalStorage <= 0 ? Infinity : parseFloat(`${state.availableStorage}`.replace(/,/g, ''))),
    totalStorage: (state: any) => state.totalStorage,
    // drag-n-drop
    dragging: (state: any) => state.dragging,
    targetDraggingId: (state: any) => state.targetDraggingId,
    sourceDraggingId: (state: any) => state.sourceDraggingId,
    addedDragEvent: (state: any) => state.addedDragEvent,
    removedDragEvent: (state: any) => state.removedDragEvent,
    canCreateItem: (state: any, getters: any, rootState: any, rootGetters: any) => getters.itemsCount < rootGetters['subscription/itemsLimit'],
    inDemo: (state: any, getters: any, rootState: any, rootGetters: any) => !!rootGetters['branches/currentBranch']?.demo_menu,
    isPOS: (state: any) => state.isPOSMenu,
  },
  mutations: {
    SET_MENU(state: any, {
      branchId, menu, itemsCount, sort, provider,
    }: any) {
      state.menuBranchId = branchId ?? state.menuBranchId
      state.itemsCount = itemsCount ?? state.itemsCount
      state.provider = provider ?? state.provider
      let normalizedMenu = mapValuesDeep(menu ?? state.menu, (value: any) => {
        const isCategory = has(value, 'parent_id')
        const node = {
          ...value, items: isPlainObject(value.items) ? [value.items] : value.items, is_category: isCategory, name: LocalizationService.getTranslate(value, 'name'),
        }
        if (!isCategory && value.price_labels) {
          node.price = (value.price_labels ?? []).filter((label: any) => label.branch_id === branchId).map((label: any) => label.price)[0] ?? 0
        }
        return node
      }, { childrenPath: ['subcategories', 'items'] })
      if (isPlainObject(normalizedMenu) && !Array.isArray(normalizedMenu)) {
        normalizedMenu = Object.values(normalizedMenu)
      }
      state.menu = MyMenuService.sortMenuBySort(normalizedMenu)
      if (sort) {
        state.sort = sort
      }
      state.filteredMenu = state.menu
      state.menuFetched = true
    },
    SET_MENU_ONLY(state: any, menu: any) {
      const normalizedMenu = Array.isArray(menu) ? menu : Object.values(menu ?? {})
      state.menu = MyMenuService.sortMenuBySort(normalizedMenu)
      state.filteredMenu = state.menu
    },
    SET_MENU_PARAMS(state: any, { query, sort, filters }: any) {
      state.query = query ?? state.query
      state.sort = sort ?? state.sort
      state.filters = filters ?? state.filters
    },
    SET_FILTERED_MENU(state: any, menu: any) {
      state.filteredMenu = menu
    },
    RESET(state: any) {
      state.query = ''
      state.sort = 'original'
      state.filters = []
    },
    SET_STORAGE(state: any, { availableStorage, totalStorage }: any) {
      state.availableStorage = availableStorage ?? state.availableStorage
      state.totalStorage = totalStorage ?? state.totalStorage
    },
    SET_DRAGGING(state: any, value = false) {
      state.dragging = value
    },
    SET_TARGET_DRAGGING(state: any, categoryId: any) {
      state.targetDraggingId = categoryId
    },
    SET_SOURCE_DRAGGING(state: any, categoryId: any) {
      state.sourceDraggingId = categoryId
    },
    SET_DRAG_EVENTS(state: any, { addedDragEvent, removedDragEvent }: any) {
      state.addedDragEvent = addedDragEvent ?? state.addedDragEvent
      state.removedDragEvent = removedDragEvent ?? state.removedDragEvent
    },
    RESET_DRAG_EVENTS(state: any) {
      state.addedDragEvent = null
      state.removedDragEvent = null
    },
    SET_IS_POS_MENU(state: any, isPOS = false) {
      state.isPOSMenu = isPOS
    },
    SET_EDIT_ORDER_MENU(state: any, {
      branchId, menu,
    }: any) {
      state.menuBranchId = branchId ?? state.menuBranchId
      state.menu = mapValuesDeep(menu ?? state.menu, (value: any) => {
        const isCategory = has(value, 'parent_id')
        const node = {
          ...value, items: isPlainObject(value.items) ? [value.items] : value.items, is_category: isCategory, name: LocalizationService.getTranslate(value, 'name'),
        }
        if (!isCategory && value.price_labels) {
          node.price = (value.price_labels ?? []).filter((label: any) => label.branch_id === branchId).map((label: any) => label.price)[0] ?? 0
        }
        return node
      }, { childrenPath: ['subcategories', 'items'] })
    },

  },
  actions: {
    async FETCH_MENU(context: any, { force, provider }: any = {}) {
      const branchId = context.rootGetters['branches/currentBranchId']

      if (context.state.provider === provider && context.state.menuFetched && context.state.menuBranchId === branchId && !force) return context.state.menu
      const { categories, items_count, sort } = await MyMenuAPIs.fetchMenu(branchId, provider === 'okMenu' ? '' : provider)
      context.commit('SET_MENU', {
        branchId, menu: categories, itemsCount: items_count, sort, provider,
      })
      return categories
    },
    async FETCH_MENU_FOR_EDITORDER(context: any, branch_id: number) {
      const branchId = branch_id
      const { categories } = await MyMenuAPIs.fetchMenuForOrders(branchId)
      context.commit('SET_EDIT_ORDER_MENU', {
        branchId, menu: categories,
      })
      return categories
    },
    async FILTER(context: any, { query, sort, filters }: any) {
      context.commit('SET_MENU_PARAMS', { query, sort, filters })
      const currentBranchId = context.rootGetters['branches/currentBranchId']
      const filteredMenu = await MyMenuService.processMenu(context.state.menu, { ...context.state }, currentBranchId)
      context.commit('SET_FILTERED_MENU', filteredMenu)
    },
    async remove(context: any, { entity, type, current }: any) {
      const response = type === 'category' ? await MyMenuAPIs.deleteCategory(entity.id, current) : await MyMenuAPIs.deleteItem(entity.id, current)
      const menu = MyMenuService.remove(context.state.menu, entity.id, type)
      await context.dispatch('UPDATE_MENU', menu)
      return response
    },
    async move(context: any, { entities, type, parentId }: any) {
      const apiType = type === 'item' ? 'items' : 'categories'
      const response = await MyMenuAPIs.move(apiType, parentId, entities.map((e: any) => e.id).join(','))
      const responseEntities = (response.categories ?? response.items) ?? entities
      let menu = { ...context.state.menu }

      responseEntities.forEach((entity: any, idx: number) => {
        menu = MyMenuService.move(menu, parentId, { ...(entities[idx] ?? {}), ...entity }, type, entities[idx]?.newIndex)
        if (entities[idx]?.newIndex > -1 && context.state.sort !== 'original') {
          context.commit('SET_MENU_PARAMS', { sort: 'original' })
        }
      })
      await context.dispatch('UPDATE_MENU', menu)
      return response
    },
    async MOVE_NODE(context: any, { node, type, parentId }: any) {
      let menu = { ...context.state.menu }
      menu = MyMenuService.move(menu, parentId, node, type, -1)
      await context.dispatch('UPDATE_MENU', menu)
    },
    async duplicate(context: any, {
      entities, type, name, parentId,
    }: any) {
      const apiType = type === 'item' ? 'items' : 'categories'
      const entitiesIds = entities.map((e: any) => e.id).join(',')
      const nameLocale = { locale: LocalizationService.getCurrentLocale(), name }
      const response = await MyMenuAPIs.duplicate(apiType, parentId, nameLocale, entitiesIds)
      const responseEntities = (response.categories ?? response.items) ?? entities

      let menu = { ...context.state.menu }

      responseEntities.forEach((entity: any, idx: number) => { menu = MyMenuService.copy(menu, parentId, { ...(entities[idx] ?? {}), ...entity }, name, type) })
      await context.dispatch('UPDATE_MENU', menu)
      return response
    },
    async PIN(context: any, { category, pin_from, pin_to }: any) {
      const pin = (!!pin_from && !!pin_to) ? 1 : 0
      const response = await MyMenuAPIs.pin(category.id, pin, pin_from, pin_to)
      const newCategory = {
        ...category,
        ...response?.category,
        name: LocalizationService.getTranslate(response?.category, 'name'),
      }
      const menu = MyMenuService.update(context.state.menu, newCategory, 'category')
      await context.dispatch('UPDATE_MENU', menu)
      return response
    },
    async ADD_CATEGORY(context: any, payload: any) {
      const response = await MyMenuAPIs.createCategory(payload)
      const { category } = response
      category.can_edit_current = true
      category.items = category.items ?? []
      category.subcategories = category.subcategories ?? []
      category.name = LocalizationService.getTranslate(category, 'name')
      category.is_category = true
      const menu = MyMenuService.add(context.state.menu, category, 'category')
      await context.dispatch('UPDATE_MENU', menu)
      return response
    },
    async ADD_ITEM(context: any, payload: any) {
      const response = await MyMenuAPIs.createItem(payload)
      const prevItemsCount = context.getters.itemsCount
      const { item } = response
      item.can_edit_current = true
      item.name = LocalizationService.getTranslate(item, 'name')
      item.is_category = false
      if (item.price_labels) {
        item.price = (item.price_labels ?? []).filter((label: any) => label.branch_id === context.rootGetters['branches/currentBranchId']).map((label: any) => label.price)[0] ?? 0
      }
      const menu = MyMenuService.add(context.state.menu, item, 'item')
      await context.dispatch('UPDATE_MENU', menu)
      if (!prevItemsCount) {
        MarketingService.triggerEvent(MARKETING_EVENTS.MENU_BUILD, 'menu', 'user add item')
      }
      return response
    },
    async UPDATE_CATEGORY(context: any, payload: any) {
      const response = await MyMenuAPIs.editCategory(payload)
      const { category } = response
      category.name = LocalizationService.getTranslate(category, 'name')
      category.is_category = true
      await context.dispatch('UPDATE_NODE', { node: category, type: 'category' })
      return response
    },
    async UPDATE_ITEM(context: any, payload: any) {
      const response = await MyMenuAPIs.editItem(payload)
      const { item } = response
      item.name = LocalizationService.getTranslate(item, 'name')
      item.description = LocalizationService.getTranslate(item, 'description')
      if (item.price_labels) {
        item.price = (item.price_labels ?? []).filter((label: any) => label.branch_id === context.rootGetters['branches/currentBranchId']).map((label: any) => label.price)[0] ?? 0
      }
      item.is_category = false
      await context.dispatch('UPDATE_NODE', { node: item, type: 'item' })
      return response
    },
    async UPDATE_MENU(context: any, menu: any) {
      context.commit('SET_MENU_ONLY', menu)
      const filteredMenu = await MyMenuService.processMenu(context.state.menu, { ...context.state })
      context.commit('SET_FILTERED_MENU', filteredMenu)
    },
    async SORT_ITEM_CATEGORY(context: any, { element, newIndex, parent }: any) {
      const isCategory = element.is_category
      const childKey = isCategory ? 'subcategories' : 'items'

      let children = parent ? [...parent[childKey]] : [...context.state.filteredMenu]
      const newElement = {
        ...element,
        sort: newIndex + 1,
      }
      children = children.filter(child => child.id !== newElement.id)
      children.splice(newIndex, 0, newElement)

      for (let i = newIndex; i < children.length; i += 1) {
        children[i].sort = i + 1
      }

      const payload: any = {
        ids: children.map(child => child.id).join(','),
      }
      if (parent?.id) {
        payload.parent = parent?.id
      }

      let menu = children

      if (parent) {
        const newParent = {
          ...parent,
          [childKey]: children,
        }
        menu = MyMenuService.update(context.state.menu, newParent, 'category')
      }
      context.commit('SET_MENU_PARAMS', { sort: 'original' })
      await context.dispatch('UPDATE_MENU', menu)

      return (isCategory ? MyMenuAPIs.sortCategory(payload) : MyMenuAPIs.sortItem(payload))
    },
    async UPDATE_NODE(context: any, { node, type }: any) {
      const menu = MyMenuService.update(context.state.menu, node, type)
      await context.dispatch('UPDATE_MENU', menu)
      return true
    },
    async REFRESH_MENU_LOCALIZATION({ state, commit }: any) {
      if (!state.menuFetched) return
      commit('SET_MENU', {})
    },
    SET_IS_POS(context: any, isPOS = false) {
      context.commit('SET_IS_POS_MENU', isPOS)
    },
  },
}
